(() => {
  if (customElements.get('mas-pdp-delivery-options')) return;
  class MasPdpDeliveryOptions extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      try {
        this.variants = new Map(JSON.parse(this.querySelector('[data-delivery-variants]').textContent).map(variant => [String(variant.id), variant]));
        this.copy = JSON.parse(this.querySelector('[data-delivery-copy]').textContent);
      } catch (_) { return; }
      if (![...this.variants.values()].some(variant => variant.requires_shipping !== false)) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.main = this.closest('.hdt-main-section--product') || this.closest('.hdt-main-product');
      this.form = document.getElementById(this.dataset.form);
      this.postcode = this.querySelector('[data-delivery-postcode]');
      this.province = this.querySelector('[data-delivery-province]');
      this.checkButton = this.querySelector('[data-delivery-check]');
      this.status = this.querySelector('[data-delivery-status]');
      this.rates = this.querySelector('[data-delivery-rates]');
      this.radios = [...this.querySelectorAll('[data-delivery-method]')];
      this.mode = this.radios.find(radio => radio.checked)?.value || 'delivery';
      this.radios.forEach(radio => radio.addEventListener('change', () => {
        this.mode = radio.value;
        this.invalidate();
        this.updateMode();
      }, {signal}));
      this.checkButton.addEventListener('click', () => this.check(), {signal});
      this.postcode.addEventListener('keydown', event => {
        if (event.key === 'Enter') { event.preventDefault(); this.check(); }
      }, {signal});
      this.postcode.addEventListener('input', () => this.invalidate(), {signal});
      this.province.addEventListener('change', () => this.invalidate(), {signal});
      this.queue = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.sync(); });
      };
      ['input', 'change', 'variant:change'].forEach(type => this.main?.addEventListener(type, event => {
        if (!this.contains(event.target)) this.queue();
      }, {signal}));
      this.observer = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target))) this.queue();
      });
      if (this.main) this.observer.observe(this.main, {subtree:true, childList:true, attributes:true, attributeFilter:['value', 'disabled', 'no-pick', 'max', 'min']});
      ['cart:change', 'cart:refresh', 'variant:add'].forEach(type => document.addEventListener(type, () => this.invalidate('cart_changed'), {signal}));
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.invalidate('cart_changed');
      }, {signal});
      window.addEventListener('pageshow', () => { this.sync(); this.invalidate(); }, {signal});
      this.sync();
      this.updateMode();
      this.hidden = false;
    }

    updateMode() {
      this.querySelectorAll('[data-delivery-panel]').forEach(panel => { panel.hidden = panel.dataset.deliveryPanel !== this.mode; });
      const note = this.querySelector('[data-delivery-selection-note]');
      const state = this.availabilityState();
      note.hidden = this.mode !== 'pickup' || !state || state === 'pickup_only';
      note.textContent = note.hidden ? '' : `${this.copy[`${state}_title`]} ${this.copy[`${state}_copy`]}`;
    }

    availabilityState() {
      if (!this.variant) return 'choose';
      if (!this.variant.available) return 'sold_out';
      if (this.variant.requires_shipping === false) return 'not_shipped';
      if (this.dataset.pickupOnly === 'true') return 'pickup_only';
      if (!this.validQuantity) return 'quantity';
      return '';
    }

    sync() {
      this.form = document.getElementById(this.dataset.form);
      const picker = this.main?.querySelector(`hdt-variant-picker[form="${CSS.escape(this.dataset.form)}"]`);
      this.variant = picker?.hasAttribute('no-pick') ? null : this.variants.get(String(this.form?.elements.namedItem('id')?.value || ''));
      const quantityInput = this.form?.elements.namedItem('quantity');
      this.quantity = Number(quantityInput?.value ?? 1);
      const min = Number(quantityInput?.min || 1);
      const max = quantityInput?.max ? Number(quantityInput.max) : Infinity;
      const step = Number(quantityInput?.step || 1);
      this.validQuantity = Number.isInteger(this.quantity) && this.quantity > 0 && this.quantity >= min && this.quantity <= max && (this.quantity - min) % step === 0;
      const signature = `${this.variant?.id || ''}:${this.quantity}:${this.validQuantity}:${this.variant?.available}`;
      if (this.signature === signature) return;
      const changed = this.signature !== undefined;
      this.signature = signature;
      this.invalidate(changed ? 'changed' : 'initial');
      this.updateMode();
    }

    setBusy(busy) {
      this.checkButton.disabled = busy || Boolean(this.availabilityState());
      this.checkButton.toggleAttribute('aria-busy', busy);
      this.checkButton.textContent = busy ? this.copy.checking : this.copy.check;
      this.status.setAttribute('aria-busy', String(busy));
      this.postcode.disabled = Boolean(this.availabilityState());
      this.province.disabled = Boolean(this.availabilityState());
    }

    invalidate(state = 'initial') {
      this.request?.abort();
      this.request = null;
      this.setBusy(false);
      this.postcode.removeAttribute('aria-invalid');
      this.province.removeAttribute('aria-invalid');
      this.showState(this.availabilityState() || state);
    }

    async cartContext(signal) {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart?section_id=mas-delivery-cart-context`, {signal, cache:'no-store', credentials:'same-origin'});
      if (!response.ok) throw new Error('Cart unavailable');
      const node = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-mas-delivery-cart]');
      const cart = JSON.parse(node?.textContent || 'null');
      if (!cart || !Array.isArray(cart.items) || !Number.isInteger(cart.item_count) || typeof cart.currency !== 'string' || cart.items.some(item => typeof item.flags !== 'string')) throw new Error('Invalid cart');
      return cart;
    }

    async pause(signal) {
      await new Promise((resolve, reject) => {
        if (signal.aborted) { reject(new Error('Cancelled')); return; }
        const aborted = () => { clearTimeout(timer); reject(new Error('Cancelled')); };
        const timer = setTimeout(() => { signal.removeEventListener('abort', aborted); resolve(); }, 750);
        signal.addEventListener('abort', aborted, {once:true});
      });
    }

    async shippingRates(params, signal) {
      const root = window.Shopify?.routes?.root || '/';
      const prepared = await fetch(`${root}cart/prepare_shipping_rates.json?${params}`, {method:'POST', signal, credentials:'same-origin'});
      if (!prepared.ok) throw new Error(prepared.status === 422 ? 'Address rejected' : 'Rates unavailable');
      for (let attempt = 0; attempt < 12; attempt++) {
        const response = await fetch(`${root}cart/async_shipping_rates.json?${params}`, {signal, cache:'no-store', credentials:'same-origin'});
        if (!response.ok) throw new Error('Rates unavailable');
        const result = await response.json();
        if (result !== null) {
          if (!Array.isArray(result.shipping_rates)) throw new Error('Invalid rates');
          return result.shipping_rates;
        }
        await this.pause(signal);
      }
      throw new Error('Rates timed out');
    }

    async check() {
      this.sync();
      if (this.request || this.mode !== 'delivery') return;
      const unavailable = this.availabilityState();
      if (unavailable) { this.showState(unavailable); return; }
      const postcode = this.postcode.value.trim();
      if (!/^\d{4}$/.test(postcode)) {
        this.postcode.setAttribute('aria-invalid', 'true');
        this.showState('invalid');
        this.postcode.focus({preventScroll:true});
        return;
      }
      if (!this.province.value) {
        this.province.setAttribute('aria-invalid', 'true');
        this.showState('province');
        this.province.focus({preventScroll:true});
        return;
      }
      const request = new AbortController();
      this.request = request;
      const timer = setTimeout(() => request.abort(), 18000);
      const isCurrent = () => this.isConnected && this.request === request;
      this.setBusy(true);
      this.showState('loading');
      const params = new URLSearchParams({'shipping_address[zip]':postcode, 'shipping_address[country]':'Australia', 'shipping_address[province]':this.province.value});
      try {
        const cart = await this.cartContext(request.signal);
        if (!isCurrent()) return;
        if (!cart.item_count) { this.showState('empty'); return; }
        if (!cart.requires_shipping) { this.showState('cart_not_shipped'); return; }
        if (cart.items.some(item => item.requires_shipping && item.flags.includes('pickup_only'))) { this.showState('cart_pickup_only'); return; }
        if (['Western Australia', 'Northern Territory', 'Tasmania'].includes(this.province.value) && cart.items.some(item => item.requires_shipping && item.flags.includes('live'))) { this.showState('restricted'); return; }
        const rates = await this.shippingRates(params, request.signal);
        const latestCart = await this.cartContext(request.signal);
        if (!isCurrent()) return;
        if (JSON.stringify(cart) !== JSON.stringify(latestCart)) { this.invalidate('cart_changed'); return; }
        this.renderRates(rates, cart.currency);
      } catch (error) {
        if (isCurrent()) this.showState(error.message === 'Address rejected' ? 'address_error' : 'error');
      } finally {
        clearTimeout(timer);
        if (this.request === request) { this.request = null; this.setBusy(false); }
      }
    }

    renderRates(rates, currency) {
      if (!rates.length) { this.showState('no_rates'); return; }
      const items = rates.map(rate => {
        if ((typeof rate.price !== 'string' && typeof rate.price !== 'number') || String(rate.price).trim() === '' || !Number.isFinite(Number(rate.price)) || Number(rate.price) < 0 || typeof (rate.presentment_name || rate.name) !== 'string') throw new Error('Invalid rate');
        const item = document.createElement('li');
        const name = document.createElement('span');
        const price = document.createElement('strong');
        name.textContent = rate.presentment_name || rate.name;
        price.textContent = new Intl.NumberFormat('en-AU', {style:'currency', currency:rate.currency || currency, currencyDisplay:'code'}).format(Number(rate.price));
        item.append(name, price);
        return item;
      });
      this.showState('success');
      this.rates.replaceChildren(...items);
      this.rates.hidden = false;
    }

    showState(state) {
      this.status.dataset.state = state;
      this.rates.replaceChildren();
      this.rates.hidden = true;
      this.querySelector('[data-delivery-result-title]').textContent = this.copy[`${state}_title`] || this.copy.initial_title;
      this.querySelector('[data-delivery-result-copy]').textContent = this.copy[`${state}_copy`] || this.copy.initial_copy;
    }

    disconnectedCallback() {
      this.request?.abort();
      this.request = null;
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.signature = undefined;
      this.hidden = true;
    }
  }
  customElements.define('mas-pdp-delivery-options', MasPdpDeliveryOptions);
})();
